'use client';
import React, { useState, useRef, useCallback, useEffect } from 'react';

type OutputFormat = 'mp3' | 'wav' | 'm4a';

const OUTPUT_FORMATS: { value: OutputFormat; label: string; mime: string; desc: string }[] = [
  { value: 'mp3', label: 'MP3', mime: 'audio/mpeg', desc: 'Best compatibility' },
  { value: 'wav', label: 'WAV', mime: 'audio/wav', desc: 'Lossless quality' },
  { value: 'm4a', label: 'M4A', mime: 'audio/mp4', desc: 'Apple devices' },
];

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
}

function formatTime(seconds: number): string {
  if (!isFinite(seconds) || seconds < 0) return '0:00';
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  return `${m}:${s.toString().padStart(2, '0')}`;
}

function formatTimeDetailed(seconds: number): string {
  if (!isFinite(seconds) || seconds < 0) return '0:00.0';
  const m = Math.floor(seconds / 60);
  const s = (seconds % 60).toFixed(1);
  return `${m}:${parseFloat(s) < 10 ? '0' : ''}${s}`;
}

function parseTime(str: string): number {
  const trimmed = str.trim();
  if (trimmed.includes(':')) {
    const parts = trimmed.split(':');
    const mins = parseFloat(parts[0]) || 0;
    const secs = parseFloat(parts[1]) || 0;
    return mins * 60 + secs;
  }
  return parseFloat(trimmed) || 0;
}

export default function AudioTrimmerTool() {
  const [audioFile, setAudioFile] = useState<{ file: File; url: string; size: number; duration: number } | null>(null);
  const [startTime, setStartTime] = useState(0);
  const [endTime, setEndTime] = useState(0);
  const [startInput, setStartInput] = useState('0:00');
  const [endInput, setEndInput] = useState('0:00');
  const [outputFormat, setOutputFormat] = useState<OutputFormat>('mp3');
  const [processing, setProcessing] = useState(false);
  const [progress, setProgress] = useState(0);
  const [progressLabel, setProgressLabel] = useState('');
  const [resultUrl, setResultUrl] = useState<string | null>(null);
  const [resultSize, setResultSize] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [isPreviewPlaying, setIsPreviewPlaying] = useState(false);
  const [validationError, setValidationError] = useState<string | null>(null);
  const [waveformData, setWaveformData] = useState<number[]>([]);
  const [isLoadingWaveform, setIsLoadingWaveform] = useState(false);
  const [dragging, setDragging] = useState<'start' | 'end' | null>(null);

  const fileInputRef = useRef<HTMLInputElement>(null);
  const audioRef = useRef<HTMLAudioElement>(null);
  const previewAudioRef = useRef<HTMLAudioElement>(null);
  const waveformRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);

  const selectedDuration = endTime - startTime;

  // Draw waveform on canvas
  useEffect(() => {
    if (!canvasRef.current || waveformData.length === 0 || !audioFile) return;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const dpr = window.devicePixelRatio || 1;
    const rect = canvas.getBoundingClientRect();
    canvas.width = rect.width * dpr;
    canvas.height = rect.height * dpr;
    ctx.scale(dpr, dpr);

    const width = rect.width;
    const height = rect.height;
    const barWidth = width / waveformData.length;
    const duration = audioFile.duration;

    const startPct = startTime / duration;
    const endPct = endTime / duration;
    const currentPct = currentTime / duration;

    ctx.clearRect(0, 0, width, height);

    // Draw bars
    waveformData.forEach((val, i) => {
      const x = i * barWidth;
      const pct = i / waveformData.length;
      const barH = Math.max(2, val * height * 0.85);
      const y = (height - barH) / 2;

      if (pct >= startPct && pct <= endPct) {
        ctx.fillStyle = '#f59e0b'; // amber - selected region
      } else {
        ctx.fillStyle = '#e5e7eb'; // gray - unselected
      }
      ctx.beginPath();
      ctx.roundRect(x + 0.5, y, Math.max(1, barWidth - 1), barH, 1);
      ctx.fill();
    });

    // Selected region overlay
    ctx.fillStyle = 'rgba(245, 158, 11, 0.08)';
    ctx.fillRect(startPct * width, 0, (endPct - startPct) * width, height);

    // Start handle line
    ctx.strokeStyle = '#d97706';
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.moveTo(startPct * width, 0);
    ctx.lineTo(startPct * width, height);
    ctx.stroke();

    // End handle line
    ctx.strokeStyle = '#b45309';
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.moveTo(endPct * width, 0);
    ctx.lineTo(endPct * width, height);
    ctx.stroke();

    // Playhead
    if (currentTime > 0) {
      ctx.strokeStyle = '#6366f1';
      ctx.lineWidth = 1.5;
      ctx.setLineDash([3, 3]);
      ctx.beginPath();
      ctx.moveTo(currentPct * width, 0);
      ctx.lineTo(currentPct * width, height);
      ctx.stroke();
      ctx.setLineDash([]);
    }
  }, [waveformData, startTime, endTime, currentTime, audioFile]);

  // Audio time update
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;
    const onTime = () => setCurrentTime(audio.currentTime);
    const onPlay = () => setIsPlaying(true);
    const onPause = () => setIsPlaying(false);
    const onEnded = () => { setIsPlaying(false); setCurrentTime(0); };
    audio.addEventListener('timeupdate', onTime);
    audio.addEventListener('play', onPlay);
    audio.addEventListener('pause', onPause);
    audio.addEventListener('ended', onEnded);
    return () => {
      audio.removeEventListener('timeupdate', onTime);
      audio.removeEventListener('play', onPlay);
      audio.removeEventListener('pause', onPause);
      audio.removeEventListener('ended', onEnded);
    };
  }, [audioFile]);

  // Preview audio events
  useEffect(() => {
    const audio = previewAudioRef.current;
    if (!audio) return;
    const onPlay = () => setIsPreviewPlaying(true);
    const onPause = () => setIsPreviewPlaying(false);
    const onEnded = () => setIsPreviewPlaying(false);
    audio.addEventListener('play', onPlay);
    audio.addEventListener('pause', onPause);
    audio.addEventListener('ended', onEnded);
    return () => {
      audio.removeEventListener('play', onPlay);
      audio.removeEventListener('pause', onPause);
      audio.removeEventListener('ended', onEnded);
    };
  }, [previewUrl]);

  const generateWaveform = useCallback(async (file: File) => {
    setIsLoadingWaveform(true);
    try {
      const arrayBuffer = await file.arrayBuffer();
      const audioCtx = new (window.AudioContext || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext)();
      const audioBuffer = await audioCtx.decodeAudioData(arrayBuffer);
      const channelData = audioBuffer.getChannelData(0);
      const samples = 120;
      const blockSize = Math.floor(channelData.length / samples);
      const data: number[] = [];
      for (let i = 0; i < samples; i++) {
        let sum = 0;
        for (let j = 0; j < blockSize; j++) {
          sum += Math.abs(channelData[i * blockSize + j]);
        }
        data.push(sum / blockSize);
      }
      const max = Math.max(...data, 0.001);
      setWaveformData(data.map(v => v / max));
      await audioCtx.close();
    } catch {
      // Fallback: generate random-ish waveform
      const fallback = Array.from({ length: 120 }, () => 0.2 + Math.random() * 0.6);
      setWaveformData(fallback);
    } finally {
      setIsLoadingWaveform(false);
    }
  }, []);

  const loadFile = useCallback((file: File) => {
    if (file.size > 500 * 1024 * 1024) {
      setError('File too large. Maximum 500 MB.');
      return;
    }
    const ext = file.name.split('.').pop()?.toLowerCase() || '';
    const supported = ['mp3', 'wav', 'm4a', 'aac', 'ogg', 'flac', 'webm'];
    if (!supported.includes(ext)) {
      setError(`Unsupported format ".${ext}". Supported: MP3, WAV, M4A, AAC, OGG, FLAC.`);
      return;
    }
    setError(null);
    setValidationError(null);
    setResultUrl(null);
    setPreviewUrl(null);
    setCurrentTime(0);
    setIsPlaying(false);
    const url = URL.createObjectURL(file);
    const audio = new Audio(url);
    audio.onloadedmetadata = () => {
      const dur = audio.duration;
      const defaultEnd = Math.min(30, dur);
      setAudioFile({ file, url, size: file.size, duration: dur });
      setStartTime(0);
      setEndTime(defaultEnd);
      setStartInput('0:00');
      setEndInput(formatTime(defaultEnd));
      generateWaveform(file);
    };
    audio.onerror = () => {
      setError('Could not read audio file. Please try a different file.');
    };
  }, [generateWaveform]);

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files?.[0];
    if (file) loadFile(file);
  };

  const validateTimes = (start: number, end: number, duration: number): string | null => {
    if (start < 0) return 'Start time cannot be negative.';
    if (end > duration) return `End time cannot exceed total duration (${formatTime(duration)}).`;
    if (start >= end) return 'Start time must be less than end time.';
    if (end - start < 0.1) return 'Selected duration must be at least 0.1 seconds.';
    return null;
  };

  const handleStartInputBlur = () => {
    if (!audioFile) return;
    const val = parseTime(startInput);
    const clamped = Math.max(0, Math.min(val, audioFile.duration - 0.1));
    setStartTime(clamped);
    setStartInput(formatTimeDetailed(clamped));
    const err = validateTimes(clamped, endTime, audioFile.duration);
    setValidationError(err);
  };

  const handleEndInputBlur = () => {
    if (!audioFile) return;
    const val = parseTime(endInput);
    const clamped = Math.max(0.1, Math.min(val, audioFile.duration));
    setEndTime(clamped);
    setEndInput(formatTimeDetailed(clamped));
    const err = validateTimes(startTime, clamped, audioFile.duration);
    setValidationError(err);
  };

  const handleWaveformMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!audioFile || !waveformRef.current) return;
    const rect = waveformRef.current.getBoundingClientRect();
    const pct = (e.clientX - rect.left) / rect.width;
    const time = pct * audioFile.duration;
    const startDist = Math.abs(time - startTime);
    const endDist = Math.abs(time - endTime);
    if (startDist < endDist) {
      setDragging('start');
    } else {
      setDragging('end');
    }
  };

  const handleWaveformMouseMove = useCallback((e: MouseEvent) => {
    if (!dragging || !audioFile || !waveformRef.current) return;
    const rect = waveformRef.current.getBoundingClientRect();
    const pct = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
    const time = pct * audioFile.duration;
    if (dragging === 'start') {
      const newStart = Math.max(0, Math.min(time, endTime - 0.1));
      setStartTime(newStart);
      setStartInput(formatTimeDetailed(newStart));
      setValidationError(validateTimes(newStart, endTime, audioFile.duration));
    } else {
      const newEnd = Math.max(startTime + 0.1, Math.min(time, audioFile.duration));
      setEndTime(newEnd);
      setEndInput(formatTimeDetailed(newEnd));
      setValidationError(validateTimes(startTime, newEnd, audioFile.duration));
    }
  }, [dragging, audioFile, startTime, endTime]);

  const handleWaveformMouseUp = useCallback(() => {
    setDragging(null);
  }, []);

  useEffect(() => {
    if (dragging) {
      window.addEventListener('mousemove', handleWaveformMouseMove);
      window.addEventListener('mouseup', handleWaveformMouseUp);
    }
    return () => {
      window.removeEventListener('mousemove', handleWaveformMouseMove);
      window.removeEventListener('mouseup', handleWaveformMouseUp);
    };
  }, [dragging, handleWaveformMouseMove, handleWaveformMouseUp]);

  // Touch support for waveform
  const handleWaveformTouchStart = (e: React.TouchEvent<HTMLDivElement>) => {
    if (!audioFile || !waveformRef.current) return;
    const touch = e.touches[0];
    const rect = waveformRef.current.getBoundingClientRect();
    const pct = (touch.clientX - rect.left) / rect.width;
    const time = pct * audioFile.duration;
    const startDist = Math.abs(time - startTime);
    const endDist = Math.abs(time - endTime);
    setDragging(startDist < endDist ? 'start' : 'end');
  };

  const handleWaveformTouchMove = (e: React.TouchEvent<HTMLDivElement>) => {
    if (!dragging || !audioFile || !waveformRef.current) return;
    e.preventDefault();
    const touch = e.touches[0];
    const rect = waveformRef.current.getBoundingClientRect();
    const pct = Math.max(0, Math.min(1, (touch.clientX - rect.left) / rect.width));
    const time = pct * audioFile.duration;
    if (dragging === 'start') {
      const newStart = Math.max(0, Math.min(time, endTime - 0.1));
      setStartTime(newStart);
      setStartInput(formatTimeDetailed(newStart));
    } else {
      const newEnd = Math.max(startTime + 0.1, Math.min(time, audioFile.duration));
      setEndTime(newEnd);
      setEndInput(formatTimeDetailed(newEnd));
    }
  };

  const togglePlayPause = () => {
    const audio = audioRef.current;
    if (!audio) return;
    if (isPlaying) {
      audio.pause();
    } else {
      audio.play();
    }
  };

  const handlePreviewSelected = async () => {
    if (!audioFile) return;
    const err = validateTimes(startTime, endTime, audioFile.duration);
    if (err) { setValidationError(err); return; }

    // Use Web Audio API to slice and play
    try {
      const arrayBuffer = await audioFile.file.arrayBuffer();
      const audioCtx = new (window.AudioContext || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext)();
      const audioBuffer = await audioCtx.decodeAudioData(arrayBuffer);
      const sampleRate = audioBuffer.sampleRate;
      const startSample = Math.floor(startTime * sampleRate);
      const endSample = Math.floor(endTime * sampleRate);
      const frameCount = endSample - startSample;
      const sliced = audioCtx.createBuffer(audioBuffer.numberOfChannels, frameCount, sampleRate);
      for (let ch = 0; ch < audioBuffer.numberOfChannels; ch++) {
        const src = audioBuffer.getChannelData(ch).subarray(startSample, endSample);
        sliced.copyToChannel(src, ch);
      }
      // Encode to WAV blob for preview
      const wavBlob = audioBufferToWav(sliced);
      if (previewUrl) URL.revokeObjectURL(previewUrl);
      const url = URL.createObjectURL(wavBlob);
      setPreviewUrl(url);
      await audioCtx.close();
      setTimeout(() => {
        previewAudioRef.current?.play();
      }, 100);
    } catch {
      setError('Could not generate preview. Try trimming directly.');
    }
  };

  const handleResetSelection = () => {
    if (!audioFile) return;
    const defaultEnd = Math.min(30, audioFile.duration);
    setStartTime(0);
    setEndTime(defaultEnd);
    setStartInput('0:00');
    setEndInput(formatTime(defaultEnd));
    setValidationError(null);
    setPreviewUrl(null);
  };

  const handleTrim = async () => {
    if (!audioFile) return;
    const err = validateTimes(startTime, endTime, audioFile.duration);
    if (err) { setValidationError(err); return; }

    setProcessing(true);
    setProgress(0);
    setError(null);
    setResultUrl(null);

    try {
      setProgressLabel('Loading FFmpeg engine…');
      setProgress(5);

      const { FFmpeg } = await import('@ffmpeg/ffmpeg');
      const { fetchFile, toBlobURL } = await import('@ffmpeg/util');
      const ffmpeg = new FFmpeg();

      ffmpeg.on('progress', ({ progress: p }) => {
        setProgress(Math.round(20 + p * 70));
        setProgressLabel(`Trimming… ${Math.round(p * 100)}%`);
      });

      setProgressLabel('Loading FFmpeg core (first time may take ~10s)…');
      setProgress(8);

      const baseURL = 'https://unpkg.com/@ffmpeg/core@0.12.6/dist/umd';
      await ffmpeg.load({
        coreURL: await toBlobURL(`${baseURL}/ffmpeg-core.js`, 'text/javascript'),
        wasmURL: await toBlobURL(`${baseURL}/ffmpeg-core.wasm`, 'application/wasm'),
      });

      setProgressLabel('Reading file…');
      setProgress(18);

      const inputExt = audioFile.file.name.split('.').pop() || 'mp3';
      const inputName = `input.${inputExt}`;
      const outputName = `trimmed.${outputFormat}`;

      await ffmpeg.writeFile(inputName, await fetchFile(audioFile.file));

      setProgressLabel('Trimming audio…');
      setProgress(20);

      const args = ['-i', inputName, '-ss', String(startTime), '-to', String(endTime)];

      if (outputFormat === 'mp3') {
        args.push('-codec:a', 'libmp3lame', '-b:a', '192k');
      } else if (outputFormat === 'm4a') {
        args.push('-codec:a', 'aac', '-b:a', '192k');
      } else if (outputFormat === 'wav') {
        args.push('-codec:a', 'pcm_s16le');
      }

      args.push(outputName);
      await ffmpeg.exec(args);

      setProgressLabel('Preparing download…');
      setProgress(95);

      const data = await ffmpeg.readFile(outputName);
      const mime = OUTPUT_FORMATS.find(f => f.value === outputFormat)?.mime || 'audio/mpeg';
      const blob = new Blob([data], { type: mime });
      setResultUrl(URL.createObjectURL(blob));
      setResultSize(blob.size);
      setProgress(100);
      setProgressLabel('Done!');
    } catch (err) {
      console.error(err);
      setError('Trimming failed. Please try again or try a different output format.');
    } finally {
      setProcessing(false);
    }
  };

  const handleDownload = () => {
    if (!resultUrl || !audioFile) return;
    const baseName = audioFile.file.name.replace(/\.[^.]+$/, '');
    const a = document.createElement('a');
    a.href = resultUrl;
    a.download = `${baseName}_trimmed.${outputFormat}`;
    a.click();
  };

  const handleReset = () => {
    if (audioFile?.url) URL.revokeObjectURL(audioFile.url);
    if (resultUrl) URL.revokeObjectURL(resultUrl);
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setAudioFile(null);
    setResultUrl(null);
    setPreviewUrl(null);
    setError(null);
    setValidationError(null);
    setProgress(0);
    setProgressLabel('');
    setWaveformData([]);
    setCurrentTime(0);
    setIsPlaying(false);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  // Waveform handle positions
  const startPct = audioFile ? (startTime / audioFile.duration) * 100 : 0;
  const endPct = audioFile ? (endTime / audioFile.duration) * 100 : 100;

  return (
    <div className="space-y-5">
      {/* Error Banner */}
      {error && (
        <div className="flex items-start gap-3 bg-red-50 border border-red-100 rounded-xl p-4">
          <svg className="w-5 h-5 text-red-500 shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" />
          </svg>
          <p className="text-red-700 text-sm flex-1">{error}</p>
          <button onClick={() => setError(null)} className="text-red-400 hover:text-red-600 shrink-0">
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" /></svg>
          </button>
        </div>
      )}

      {/* Upload Area */}
      {!audioFile ? (
        <div
          className={`rounded-2xl border-2 border-dashed p-10 text-center cursor-pointer transition-all duration-200 ${
            isDragging ? 'border-amber-400 bg-amber-50 scale-[1.01]' : 'border-gray-200 bg-gray-50 hover:border-amber-300 hover:bg-amber-50/40'
          }`}
          onClick={() => fileInputRef.current?.click()}
          onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
          onDragLeave={() => setIsDragging(false)}
          onDrop={handleDrop}
          role="button"
          aria-label="Upload audio file"
        >
          <div className="w-16 h-16 rounded-2xl bg-amber-50 border border-amber-100 flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-amber-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M9 9l10.5-3m0 6.553v3.75a2.25 2.25 0 01-1.632 2.163l-1.32.377a1.803 1.803 0 11-.99-3.467l2.31-.66a2.25 2.25 0 001.632-2.163zm0 0V2.25L9 5.25v10.303m0 0v3.75a2.25 2.25 0 01-1.632 2.163l-1.32.377a1.803 1.803 0 01-.99-3.467l2.31-.66A2.25 2.25 0 009 15.553z" />
            </svg>
          </div>
          <p className="font-semibold text-gray-800 mb-1 text-base">Upload Audio File</p>
          <p className="text-gray-500 text-sm mb-3">Drag & drop or click to browse</p>
          <div className="inline-flex flex-wrap justify-center gap-1.5">
            {['MP3', 'WAV', 'M4A', 'AAC', 'OGG', 'FLAC'].map(fmt => (
              <span key={fmt} className="px-2 py-0.5 bg-white border border-gray-200 rounded-md text-xs text-gray-500 font-medium">{fmt}</span>
            ))}
          </div>
          <input ref={fileInputRef} type="file" accept=".mp3,.wav,.m4a,.aac,.ogg,.flac,.webm" className="hidden" onChange={(e) => { const f = e.target.files?.[0]; if (f) loadFile(f); }} />
        </div>
      ) : (
        <div className="space-y-4">
          {/* File Info */}
          <div className="flex items-center gap-3 bg-amber-50 border border-amber-100 rounded-xl p-4">
            <div className="w-10 h-10 rounded-xl bg-amber-100 flex items-center justify-center shrink-0">
              <svg className="w-5 h-5 text-amber-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M9 9l10.5-3m0 6.553v3.75a2.25 2.25 0 01-1.632 2.163l-1.32.377a1.803 1.803 0 11-.99-3.467l2.31-.66a2.25 2.25 0 001.632-2.163zm0 0V2.25L9 5.25v10.303m0 0v3.75a2.25 2.25 0 01-1.632 2.163l-1.32.377a1.803 1.803 0 01-.99-3.467l2.31-.66A2.25 2.25 0 009 15.553z" />
              </svg>
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-semibold text-gray-800 text-sm truncate">{audioFile.file.name}</p>
              <p className="text-gray-500 text-xs">{formatBytes(audioFile.size)} · Total: {formatTime(audioFile.duration)}</p>
            </div>
            <button onClick={handleReset} className="text-gray-400 hover:text-red-500 transition-colors p-1 shrink-0" title="Remove file">
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" /></svg>
            </button>
          </div>

          {/* Large file warning */}
          {audioFile.size > 50 * 1024 * 1024 && (
            <div className="flex items-start gap-2 bg-yellow-50 border border-yellow-200 rounded-xl p-3">
              <span className="text-yellow-500 shrink-0">⚠️</span>
              <p className="text-yellow-700 text-xs">Large audio file detected. Processing may take longer because everything runs in your browser.</p>
            </div>
          )}

          {/* Waveform + Play Controls */}
          <div className="bg-gray-50 border border-gray-100 rounded-xl p-4 space-y-3">
            <div className="flex items-center justify-between mb-1">
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Waveform Preview</p>
              <div className="flex items-center gap-2">
                <span className="text-xs text-gray-400">{formatTime(currentTime)} / {formatTime(audioFile.duration)}</span>
                <button
                  onClick={togglePlayPause}
                  className="w-8 h-8 rounded-full bg-amber-500 hover:bg-amber-600 text-white flex items-center justify-center transition-colors shrink-0"
                  title={isPlaying ? 'Pause' : 'Play'}
                >
                  {isPlaying ? (
                    <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 24 24"><path d="M6 4h4v16H6V4zm8 0h4v16h-4V4z"/></svg>
                  ) : (
                    <svg className="w-3.5 h-3.5 ml-0.5" fill="currentColor" viewBox="0 0 24 24"><path d="M8 5v14l11-7z"/></svg>
                  )}
                </button>
              </div>
            </div>

            {/* Waveform Canvas with drag handles */}
            <div
              ref={waveformRef}
              className="relative h-20 cursor-col-resize select-none"
              onMouseDown={handleWaveformMouseDown}
              onTouchStart={handleWaveformTouchStart}
              onTouchMove={handleWaveformTouchMove}
              onTouchEnd={() => setDragging(null)}
            >
              {isLoadingWaveform ? (
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="flex gap-1">
                    {Array.from({ length: 12 }).map((_, i) => (
                      <div key={i} className="w-1 bg-amber-200 rounded-full animate-pulse" style={{ height: `${20 + Math.random() * 40}px`, animationDelay: `${i * 0.1}s` }} />
                    ))}
                  </div>
                </div>
              ) : (
                <canvas ref={canvasRef} className="w-full h-full rounded-lg" style={{ display: 'block' }} />
              )}

              {/* Start handle */}
              <div
                className="absolute top-0 bottom-0 w-4 flex items-center justify-center cursor-ew-resize z-10"
                style={{ left: `calc(${startPct}% - 8px)` }}
              >
                <div className="w-3 h-8 bg-amber-500 rounded-sm shadow-md flex items-center justify-center">
                  <div className="w-0.5 h-4 bg-white/70 rounded-full" />
                </div>
              </div>

              {/* End handle */}
              <div
                className="absolute top-0 bottom-0 w-4 flex items-center justify-center cursor-ew-resize z-10"
                style={{ left: `calc(${endPct}% - 8px)` }}
              >
                <div className="w-3 h-8 bg-amber-700 rounded-sm shadow-md flex items-center justify-center">
                  <div className="w-0.5 h-4 bg-white/70 rounded-full" />
                </div>
              </div>
            </div>

            <p className="text-xs text-gray-400 text-center">Drag the handles to set start/end points</p>

            {/* Hidden audio element */}
            <audio ref={audioRef} src={audioFile.url} className="hidden" />
          </div>

          {/* Duration Info Bar */}
          <div className="grid grid-cols-3 gap-3">
            <div className="bg-gray-50 rounded-xl p-3 text-center">
              <p className="text-xs text-gray-400 mb-0.5">Total Duration</p>
              <p className="font-bold text-gray-800 text-sm">{formatTime(audioFile.duration)}</p>
            </div>
            <div className="bg-amber-50 rounded-xl p-3 text-center border border-amber-100">
              <p className="text-xs text-amber-600 mb-0.5">Selected</p>
              <p className="font-bold text-amber-700 text-sm">{formatTimeDetailed(selectedDuration)}</p>
            </div>
            <div className="bg-gray-50 rounded-xl p-3 text-center">
              <p className="text-xs text-gray-400 mb-0.5">Remaining</p>
              <p className="font-bold text-gray-800 text-sm">{formatTime(audioFile.duration - selectedDuration)}</p>
            </div>
          </div>

          {/* Manual Time Inputs */}
          <div className="bg-white border border-gray-100 rounded-xl p-4 space-y-3">
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Manual Time Entry</p>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-semibold text-gray-700 block mb-1.5">
                  Start Time
                  <span className="text-gray-400 font-normal ml-1">(m:ss)</span>
                </label>
                <input
                  type="text"
                  value={startInput}
                  onChange={(e) => setStartInput(e.target.value)}
                  onBlur={handleStartInputBlur}
                  onKeyDown={(e) => { if (e.key === 'Enter') handleStartInputBlur(); }}
                  placeholder="0:00"
                  className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:border-amber-400 focus:ring-1 focus:ring-amber-100"
                />
              </div>
              <div>
                <label className="text-sm font-semibold text-gray-700 block mb-1.5">
                  End Time
                  <span className="text-gray-400 font-normal ml-1">(m:ss)</span>
                </label>
                <input
                  type="text"
                  value={endInput}
                  onChange={(e) => setEndInput(e.target.value)}
                  onBlur={handleEndInputBlur}
                  onKeyDown={(e) => { if (e.key === 'Enter') handleEndInputBlur(); }}
                  placeholder="0:30"
                  className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:border-amber-400 focus:ring-1 focus:ring-amber-100"
                />
              </div>
            </div>

            {validationError && (
              <div className="flex items-center gap-2 bg-red-50 border border-red-100 rounded-lg p-2.5">
                <svg className="w-4 h-4 text-red-500 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z" />
                </svg>
                <p className="text-red-600 text-xs">{validationError}</p>
              </div>
            )}

            <div className="flex gap-2">
              <button
                onClick={handleResetSelection}
                className="flex items-center gap-1.5 px-3 py-2 text-xs font-semibold text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
              >
                <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0l3.181 3.183a8.25 8.25 0 0013.803-3.7M4.031 9.865a8.25 8.25 0 0113.803-3.7l3.181 3.182m0-4.991v4.99" />
                </svg>
                Reset Selection
              </button>
              <button
                onClick={handlePreviewSelected}
                className="flex items-center gap-1.5 px-3 py-2 text-xs font-semibold text-amber-700 bg-amber-50 hover:bg-amber-100 border border-amber-200 rounded-lg transition-colors"
              >
                <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M5.25 5.653c0-.856.917-1.398 1.667-.986l11.54 6.347a1.125 1.125 0 010 1.972l-11.54 6.347a1.125 1.125 0 01-1.667-.986V5.653z" />
                </svg>
                Preview Selected Part
              </button>
            </div>
          </div>

          {/* Preview Selected Part Player */}
          {previewUrl && (
            <div className="bg-amber-50 border border-amber-100 rounded-xl p-4 space-y-2">
              <div className="flex items-center gap-2">
                <span className="text-amber-600 text-sm">🎵</span>
                <p className="text-sm font-semibold text-amber-800">Preview: Selected Part</p>
                {isPreviewPlaying && <span className="text-xs text-amber-600 animate-pulse">Playing…</span>}
              </div>
              <audio ref={previewAudioRef} src={previewUrl} controls className="w-full h-10" />
            </div>
          )}

          {/* Output Format */}
          <div className="bg-white border border-gray-100 rounded-xl p-4">
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">Output Format</p>
            <div className="grid grid-cols-3 gap-2">
              {OUTPUT_FORMATS.map(fmt => (
                <button
                  key={fmt.value}
                  onClick={() => setOutputFormat(fmt.value)}
                  className={`p-3 rounded-xl border-2 text-center transition-all ${
                    outputFormat === fmt.value
                      ? 'border-amber-400 bg-amber-50' :'border-gray-100 bg-gray-50 hover:border-amber-200'
                  }`}
                >
                  <p className={`font-bold text-sm ${outputFormat === fmt.value ? 'text-amber-700' : 'text-gray-700'}`}>{fmt.label}</p>
                  <p className="text-xs text-gray-400 mt-0.5">{fmt.desc}</p>
                </button>
              ))}
            </div>
          </div>

          {/* Progress */}
          {processing && (
            <div className="space-y-1.5">
              <div className="flex justify-between text-xs text-gray-500">
                <span>{progressLabel}</span>
                <span>{progress}%</span>
              </div>
              <div className="h-2.5 bg-gray-100 rounded-full overflow-hidden">
                <div
                  className="h-full bg-gradient-to-r from-amber-400 to-amber-600 rounded-full transition-all duration-300"
                  style={{ width: `${progress}%` }}
                />
              </div>
            </div>
          )}

          {/* Trim Button */}
          {!resultUrl && (
            <button
              onClick={handleTrim}
              disabled={processing || !!validationError}
              className="w-full py-3.5 rounded-xl font-semibold text-white bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-sm shadow-amber-200 flex items-center justify-center gap-2"
            >
              {processing ? (
                <>
                  <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                  </svg>
                  Trimming…
                </>
              ) : (
                <>
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M7.848 8.25l1.536.887M7.848 8.25a3 3 0 11-5.196-3 3 3 0 015.196 3zm1.536.887a2.165 2.165 0 011.083 1.839c.005.351.054.695.14 1.024M9.384 9.137l2.077 1.199M7.848 15.75l1.536-.887m-1.536.887a3 3 0 11-5.196 3 3 3 0 015.196-3zm1.536-.887a2.165 2.165 0 001.083-1.838c.005-.352.054-.695.14-1.025m-1.223 2.863l2.077-1.199m0-3.328a4.323 4.323 0 012.068-1.379l5.325-1.628a4.5 4.5 0 012.48-.044l.803.215-7.794 4.5m-2.882-1.664A4.331 4.331 0 0010.607 12m3.736 0l7.794 4.5-.802.215a4.5 4.5 0 01-2.48-.043l-5.326-1.629a4.324 4.324 0 01-2.068-1.379M14.343 12l-2.882 1.664" />
                  </svg>
                  Trim Audio ({outputFormat.toUpperCase()})
                </>
              )}
            </button>
          )}

          {/* Result */}
          {resultUrl && (
            <div className="bg-green-50 border border-green-100 rounded-xl p-4 space-y-3">
              <div className="flex items-center gap-2">
                <svg className="w-5 h-5 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <p className="font-semibold text-green-800 text-sm">Trimmed successfully!</p>
                <span className="ml-auto text-xs text-green-600 font-medium">{formatBytes(resultSize)}</span>
              </div>
              <audio src={resultUrl} controls className="w-full h-10" />
              <div className="flex gap-2">
                <button
                  onClick={handleDownload}
                  className="flex-1 py-2.5 rounded-xl font-semibold text-white bg-green-600 hover:bg-green-700 transition-colors flex items-center justify-center gap-2 text-sm"
                >
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5M16.5 12L12 16.5m0 0L7.5 12m4.5 4.5V3" />
                  </svg>
                  Download {outputFormat.toUpperCase()}
                </button>
                <button
                  onClick={() => { setResultUrl(null); setProgress(0); }}
                  className="px-4 py-2.5 rounded-xl font-semibold text-gray-600 bg-gray-100 hover:bg-gray-200 transition-colors text-sm"
                >
                  Trim Again
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// Helper: encode AudioBuffer to WAV Blob
function audioBufferToWav(buffer: AudioBuffer): Blob {
  const numChannels = buffer.numberOfChannels;
  const sampleRate = buffer.sampleRate;
  const format = 1; // PCM
  const bitDepth = 16;
  const bytesPerSample = bitDepth / 8;
  const blockAlign = numChannels * bytesPerSample;
  const byteRate = sampleRate * blockAlign;
  const dataLength = buffer.length * blockAlign;
  const arrayBuffer = new ArrayBuffer(44 + dataLength);
  const view = new DataView(arrayBuffer);

  const writeString = (offset: number, str: string) => {
    for (let i = 0; i < str.length; i++) view.setUint8(offset + i, str.charCodeAt(i));
  };

  writeString(0, 'RIFF');
  view.setUint32(4, 36 + dataLength, true);
  writeString(8, 'WAVE');
  writeString(12, 'fmt ');
  view.setUint32(16, 16, true);
  view.setUint16(20, format, true);
  view.setUint16(22, numChannels, true);
  view.setUint32(24, sampleRate, true);
  view.setUint32(28, byteRate, true);
  view.setUint16(32, blockAlign, true);
  view.setUint16(34, bitDepth, true);
  writeString(36, 'data');
  view.setUint32(40, dataLength, true);

  let offset = 44;
  for (let i = 0; i < buffer.length; i++) {
    for (let ch = 0; ch < numChannels; ch++) {
      const sample = Math.max(-1, Math.min(1, buffer.getChannelData(ch)[i]));
      view.setInt16(offset, sample < 0 ? sample * 0x8000 : sample * 0x7fff, true);
      offset += 2;
    }
  }

  return new Blob([arrayBuffer], { type: 'audio/wav' });
}
